import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * ThemeProvider — three-way toggle: 'light' | 'dark' | 'auto'.
 * • Persists selection in localStorage.
 * • 'auto' → follows the OS `prefers-color-scheme` and reacts to changes.
 * • Applies/removes the `dark` class on <html> so Tailwind's `dark:`
 *   variants take effect app-wide (tailwind.config.js `darkMode:['class']`).
 * • OLED-friendly: dark background falls back to pure #000 for AMOLED
 *   screens, with card surfaces slightly lifted for hierarchy.
 */

const ThemeCtx = createContext(null);
const STORAGE_KEY = "rivones_theme";
const VALID_MODES = ["light", "dark", "auto"];

function resolveEffectiveMode(mode) {
  if (mode === "dark") return "dark";
  if (mode === "light") return "light";
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}

function applyThemeClass(effective) {
  const root = document.documentElement;
  root.classList.toggle("dark", effective === "dark");
  root.style.colorScheme = effective;
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      // Day (light) mode is the product default — dark/auto are opt-in via
      // the theme toggle and persist once the user picks them.
      return VALID_MODES.includes(v) ? v : "light";
    } catch (_) { return "light"; }
  });
  const [effective, setEffective] = useState(() => resolveEffectiveMode(mode));

  useEffect(() => {
    applyThemeClass(effective);
  }, [effective]);

  useEffect(() => {
    setEffective(resolveEffectiveMode(mode));
    try { localStorage.setItem(STORAGE_KEY, mode); } catch (_) { /* ignore */ }
  }, [mode]);

  // React to OS-level scheme changes when in 'auto' mode.
  useEffect(() => {
    if (mode !== "auto" || typeof window === "undefined") return;
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const handler = () => setEffective(resolveEffectiveMode("auto"));
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [mode]);

  const setMode = useCallback((next) => {
    if (VALID_MODES.includes(next)) setModeState(next);
  }, []);

  const value = useMemo(() => ({ mode, effective, setMode }), [mode, effective, setMode]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
  return useContext(ThemeCtx) || { mode: "light", effective: "light", setMode: () => {} };
}
